import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  RichTextEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import { Decimal, normalizeSku, skuFromName } from '@mlm/shared';
import {
  addHouseVariant,
  apiErrorMessage,
  apiErrorPayload,
  createProduct,
  deleteProductImage,
  getKitComponents,
  getProductCostSuggestion,
  getProductMembershipRequirements,
  getProductVariations,
  getTenantFeatures,
  listBranches,
  listCategories,
  listReplicatedPages,
  listHouseVariants,
  type HouseVariant,
  listMemberships,
  listProductSuppliers,
  listProducts,
  listSuppliers,
  listVariationTypes,
  productImageUrl,
  saveProductMembershipRequirements,
  setProductSuppliers,
  setHouseVariantStock,
  setKitComponents,
  setProductVariations,
  updateHouseVariant,
  updateProduct,
  uploadProductImage,
  type Category,
  type Membership,
  type Product,
  type ProductVariationSelection,
  type SkuUsage,
} from '../lib/api';
import { hasRole } from '../lib/auth-store';
import {
  PerProductPricingEditor,
  VariantDiscountEditor,
} from '../components/products/pricing-editor';
import { ProductRealtimeUnilevelEditor } from '../components/products/realtime-unilevel-product-editor';
import { ProductTestimonialsSection } from '../components/product-testimonials-section';
import { ProductPicker } from '../components/product-picker';

/**
 * One row of the kit/bundle inclusions editor. Named apart from the API's
 * `KitComponentRow` on purpose — this carries DISPLAY-ONLY fields the PUT body
 * has no place for, and two near-identical same-named types is how a save-time
 * shape bug ships.
 */
type ComponentDraft = {
  componentProductId: string;
  /** Names one VARIATION of the component; null = the whole product. */
  variantId: string | null;
  qtyPerKit: number;
  /** Display only — mirrors the joined columns GET /components returns, so an
   *  already-saved (possibly archived) component labels itself without a
   *  catalog fetch. STRIPPED on save. */
  sku: string;
  name: string;
  active: boolean;
};

/**
 * Server codes the product dialog presents itself, via `SkuRenameConfirmDialog`.
 *
 * ONE list feeding two suppressions — the mutation's `meta.silentErrorCodes`
 * (which only silences the global toast) AND the inline `error=` prop on the
 * form. They used to disagree: the toast was suppressed while the raw slug still
 * rendered in red under the fields, next to the dialog explaining it.
 */
const SKU_RENAME_SILENT = ['sku_in_use'];

/**
 * A per-currency sticker price must be a real, positive amount. "Not sold in
 * this currency" is the gc/coinPurchasable toggle being OFF — never a 0, which
 * the checkout gate would happily charge as free.
 */
function isPositiveMoney(value: string): boolean {
  const s = value.trim();
  if (s === '') return false;
  try {
    return new Decimal(s).gt(0);
  } catch {
    return false;
  }
}

/** The editable detail fields, shared by the create dialog (step 1) and edit. */
type ProductDetails = {
  sku: string;
  /** Acknowledges the "this SKU is already referenced" 409 — set by the confirm dialog. */
  confirmSkuRename?: boolean;
  name: string;
  description?: string;
  detailsHtml?: string | null;
  videoUrl?: string | null;
  price: string;
  pv: string;
  /** Accounting Phase 1: unit cost (COGS basis). Empty string → null (unknown). */
  cost?: string | null;
  /** Accounting Phase 2: tax classification (omitted when the accounting flag is off). */
  taxCode?: 'vatable' | 'exempt' | 'zero_rated';
  priceGc?: string | null;
  priceCoin?: string | null;
  /** Whether the product is offered for sale in each wallet currency. Always
   *  sent (not optional): omitting it on a PATCH would leave the old value. */
  gcPurchasable: boolean;
  coinPurchasable: boolean;
  coinGrant?: string;
  /** Assistant answers granted per unit; null = none. */
  aiCreditReward?: number | null;
  binaryPoints?: string;
  vortexToken?: string;
  weightKg: string;
  lengthCm?: string;
  widthCm?: string;
  heightCm?: string;
  categoryId?: string | null;
  isKit?: boolean;
  isBundle?: boolean;
  kitMembershipId?: string | null;
  kitSlotType?: 'paid' | 'free' | 'cd' | 'half_cd';
  kitUsageMode?: 'activation' | 'upgrade' | 'both';
  /** CD/Half-CD kits: the debt the minted slot starts with (required > 0). */
  kitDebtAmount?: string | null;
  platformSold?: 'cashier' | 'ecom' | 'both';
  soldTo?: 'activated' | 'non_activated' | 'both';
  featured?: boolean;
  redeemOnly?: boolean;
  freeShipping?: boolean;
  isVirtual?: boolean;
  /** Campaign page a virtual product opens instead of its product page. */
  replicatedPageId?: string | null;
};

/**
 * Admin Products page. List + filters + a single tabbed create/edit dialog.
 *  - Create and edit share `ProductDialog`: Product info / Images & Inclusions /
 *    Discount / Variations / Realtime Unilevel tabs. On create, the info tab
 *    persists the product first (so the other tabs have an id to key off).
 *  - `isKit` marks a membership kit and hides Binary points (a repurchase
 *    mechanic that doesn't apply to an activation kit).
 *  - Archive = set active=false; products can't be hard-deleted because of
 *    FK references from `kit_codes`, `order_items`, `inventory`.
 */
/**
 * Virtual products (services / digital goods) get their own page rather than
 * sharing the physical catalog: they carry no dimensions, no stock and no
 * shipping, so half the form and most of the list columns are noise for them.
 * The two pages PARTITION the catalog — `/products` excludes virtual rows and
 * `/products/virtual` shows only those — so nothing appears in both.
 */
export function VirtualProductsPage() {
  // The sidebar entry is already gated, but the URL is still typeable and
  // bookmarkable — say so plainly rather than rendering an empty catalog that
  // looks like data loss.
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  if (featuresQuery.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (featuresQuery.data?.virtual_products_enabled !== true) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Virtual products are turned off for this tenant. Turn on{' '}
          <strong>Enable virtual products</strong> in Settings → System to manage services and
          digital goods here.
        </CardContent>
      </Card>
    );
  }
  return <ProductsPage catalog="virtual" />;
}

export function ProductsPage({ catalog = 'physical' }: { catalog?: 'physical' | 'virtual' } = {}) {
  const isVirtualCatalog = catalog === 'virtual';
  const queryClient = useQueryClient();
  // Master switch. While it's off there is no virtual-products concept in
  // admin — and crucially the main catalog stops excluding virtual rows, so any
  // product already flagged `is_virtual` stays visible and editable here instead
  // of being orphaned on a page nobody can reach.
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const virtualEnabled = featuresQuery.data?.virtual_products_enabled === true;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const [kitFilter, setKitFilter] = useState<'all' | 'kit' | 'non_kit'>('all');
  const [channelFilter, setChannelFilter] = useState<'all' | 'normal' | 'redeem'>('all');
  const [categoryId, setCategoryId] = useState<string>('all');

  const productsQuery = useQuery({
    // `catalog` keys the cache — the two pages fetch disjoint sets and must not
    // serve each other's rows.
    queryKey: ['admin-products', catalog, virtualEnabled, search],
    queryFn: () =>
      listProducts({
        pageSize: 100,
        search: search || undefined,
        // Feature off ⇒ one undivided catalog, exactly as before this page existed.
        virtual: !virtualEnabled ? 'all' : isVirtualCatalog ? 'only' : 'exclude',
      }),
    // Don't fetch under the wrong partition while the flag is still loading.
    enabled: featuresQuery.isSuccess,
  });
  const membershipsQuery = useQuery({ queryKey: ['memberships'], queryFn: listMemberships });
  const categoriesQuery = useQuery({ queryKey: ['admin-categories'], queryFn: listCategories });

  const [creating, setCreating] = useState(false);
  // Hold the selected product OBJECT (not just an id) so the edit dialog never
  // loses its product to a list refetch / search filter / pagination — which used
  // to flip the dialog into create mode and POST a duplicate SKU.
  const [editing, setEditing] = useState<Product | null>(null);

  const visible = (productsQuery.data ?? []).filter((p) => {
    if (status === 'active' && !p.active) return false;
    if (status === 'archived' && p.active) return false;
    if (kitFilter === 'kit' && !p.isKit) return false;
    if (kitFilter === 'non_kit' && p.isKit) return false;
    if (channelFilter === 'redeem' && !p.redeemOnly) return false;
    if (channelFilter === 'normal' && p.redeemOnly) return false;
    if (categoryId !== 'all' && p.categoryId !== categoryId) return false;
    return true;
  });

  // The admin list (`/admin/products`) includes archived rows, so we can show
  // how many are hidden by the default "Active" view — otherwise archived
  // products are invisible unless the admin happens to change the Status filter.
  const archivedCount = (productsQuery.data ?? []).filter((p) => !p.active).length;

  const toggleActive = useMutation({
    mutationFn: (p: Product) => updateProduct(p.id, { active: !p.active }),
    meta: { successMessage: 'Product updated.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-products'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {isVirtualCatalog ? 'Virtual products' : 'Products'}
          </h1>
          {isVirtualCatalog && (
            <p className="text-sm text-muted-foreground">
              Services and digital goods: no dimensions, no stock, no shipping. Physical products
              are on the Products page.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {status === 'active' && archivedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setStatus('archived')}>
              View {archivedCount} archived
            </Button>
          )}
          <Button onClick={() => setCreating(true)}>
            + New {isVirtualCatalog ? 'virtual product' : 'product'}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs text-muted-foreground">Search</label>
            <Input
              placeholder="Name or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <FilterSelect
            label="Status"
            value={status}
            onChange={(v) => setStatus(v as typeof status)}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'archived', label: 'Archived' },
              { value: 'all', label: 'All' },
            ]}
          />
          <FilterSelect
            label="Kit"
            value={kitFilter}
            onChange={(v) => setKitFilter(v as typeof kitFilter)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'kit', label: 'Kits only' },
              { value: 'non_kit', label: 'Non-kit' },
            ]}
          />
          <FilterSelect
            label="Channel"
            value={channelFilter}
            onChange={(v) => setChannelFilter(v as typeof channelFilter)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'normal', label: 'Normal store' },
              { value: 'redeem', label: 'Redeem (GC)' },
            ]}
          />
          <FilterSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={[
              { value: 'all', label: 'All' },
              ...(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        </CardContent>
      </Card>

      {creating && (
        <ProductDialog
          mode="create"
          memberships={membershipsQuery.data ?? []}
          categories={categoriesQuery.data ?? []}
          virtualCatalog={isVirtualCatalog}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <ProductDialog
          mode="edit"
          key={editing.id}
          // Prefer the fresh row from the list, but fall back to the object we
          // captured on click so the dialog always has its product.
          product={(productsQuery.data ?? []).find((p) => p.id === editing.id) ?? editing}
          memberships={membershipsQuery.data ?? []}
          categories={categoriesQuery.data ?? []}
          virtualCatalog={isVirtualCatalog}
          onClose={() => setEditing(null)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Catalog</CardTitle>
        </CardHeader>
        <CardContent>
          {/* The product fetch waits on the feature flag (it decides the
              partition), so a bare `productsQuery.isLoading` would flash
              "No products match" while the flag is still in flight. */}
          {featuresQuery.isLoading || productsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">No products match.</p>
          ) : (
            <ul className="divide-y">
              {visible.map((p) => (
                <li key={p.id} className="py-3">
                  <ProductRow
                    product={p}
                    memberships={membershipsQuery.data ?? []}
                    onEdit={() => setEditing(p)}
                    onToggleActive={() => toggleActive.mutate(p)}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ProductRow({
  product,
  memberships,
  onEdit,
  onToggleActive,
}: {
  product: Product;
  memberships: Membership[];
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  const tier =
    product.kitMembershipId && memberships.find((m) => m.id === product.kitMembershipId)?.name;
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="h-10 w-10 shrink-0 rounded border bg-muted overflow-hidden">
        {(product.images?.length ?? 0) > 0 && (
          <img
            src={`${productImageUrl(product.id, 0)}?v=${encodeURIComponent(product.images[0]!)}`}
            alt=""
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{product.name}</span>
          {product.isKit && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-info/10 text-info">
              Kit{tier ? ` · ${tier}` : ''}
            </span>
          )}
          {product.redeemOnly && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">
              Redeem (GC)
            </span>
          )}
          {!product.active && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Archived
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {product.redeemOnly
            ? // An em-dash, not "0.00 GC": a product that isn't sold in GC has no
              // GC price, and rendering a zero reads as "free".
              `${product.sku} · ${
                product.gcPurchasable && product.priceGc
                  ? `${new Decimal(product.priceGc).toFixed(2)} GC`
                  : '—'
              }`
            : `${product.sku} · ₱${Number(product.price).toFixed(2)} · PV ${Number(product.pv).toFixed(2)}`}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onEdit}>
        Edit
      </Button>
      <Button variant="outline" size="sm" onClick={onToggleActive}>
        {product.active ? 'Archive' : 'Restore'}
      </Button>
    </div>
  );
}

/**
 * Unified create/edit dialog with tabs. Replaces the old 2-step create wizard
 * and the inline edit card.
 *  - Product info / Images & Inclusions / Discount / Variations / Realtime Unilevel.
 *  - On CREATE the product doesn't exist until the info tab is saved, so the
 *    other tabs are disabled until then; saving persists it (kept on close) and
 *    advances to the media tab. Each non-info tab saves independently.
 *  - Variations show only for non-kit products; the Realtime Unilevel tab shows
 *    only to super_admin when the realtime_unilevel plan is enabled.
 */
function ProductDialog({
  mode,
  product,
  memberships,
  categories,
  virtualCatalog,
  onClose,
}: {
  mode: 'create' | 'edit';
  product?: Product;
  memberships: Membership[];
  categories: Category[];
  /** Opened from the Virtual products page: force `isVirtual` and trim the form. */
  virtualCatalog?: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const unilevelEnabled = !new Set(featuresQuery.data?.disabled_plans ?? []).has(
    'realtime_unilevel',
  );
  const showUnilevel = hasRole('super_admin') && unilevelEnabled;
  const virtualFeatureEnabled = featuresQuery.data?.virtual_products_enabled === true;

  // In create mode the product is persisted on first info-save and held here so
  // the remaining tabs (which key off its id) can be enabled. In edit mode the
  // live product is the prop, refreshed by the parent on cache invalidation.
  const [created, setCreated] = useState<Product | null>(null);
  const current = product ?? created;
  const [tab, setTab] = useState('info');
  // Rich Details HTML is lifted here so it survives tab switches (Radix unmounts
  // inactive tab bodies) and is submitted together with the info form on create.
  const [detailsHtml, setDetailsHtml] = useState(product?.detailsHtml ?? '');

  const create = useMutation({
    mutationFn: (details: ProductDetails) =>
      createProduct({ ...details, price: details.price.trim(), pv: details.pv.trim() }),
    meta: { successMessage: 'Product created.' },
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      setCreated(p);
      setTab('media');
    },
  });
  // A SKU rename on a product that already has orders / stock / POs comes back as
  // 409 `sku_in_use`. The rename is data-safe (nothing stores the code, only ids)
  // — the warning is about printed barcodes and price tags in the wild — so we
  // park the payload and re-send it with `confirmSkuRename` once the admin agrees.
  const [skuRenameConfirm, setSkuRenameConfirm] = useState<{
    details: ProductDetails;
    usage?: SkuUsage;
  } | null>(null);

  const update = useMutation({
    mutationFn: (details: ProductDetails) => updateProduct(current!.id, details),
    meta: { successMessage: 'Product saved.', silentErrorCodes: SKU_RENAME_SILENT },
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      if (created) setCreated(p);
      setSkuRenameConfirm(null);
    },
    onError: (err, details) => {
      const payload = apiErrorPayload(err);
      if (payload?.error === 'sku_in_use') {
        setSkuRenameConfirm({ details, usage: payload.usage as SkuUsage | undefined });
      }
    },
  });
  // The confirm dialog IS the presentation of this error — rendering it a second
  // time as red text under the form fields is the bug being fixed. Must be
  // declared after `update`, not beside the state above (TDZ).
  const skuRenameHandled = SKU_RENAME_SILENT.includes(apiErrorPayload(update.error)?.error ?? '');
  // Edit-mode independent save for the Details tab (PATCH accepts a partial).
  const saveDetails = useMutation({
    mutationFn: () =>
      updateProduct(current!.id, { detailsHtml: detailsHtml.trim() === '' ? null : detailsHtml }),
    meta: { successMessage: 'Details saved.' },
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      if (created) setCreated(p);
    },
  });

  // Every tab body shares one fixed height + internal scroll so switching tabs
  // doesn't resize the dialog (the info form is much taller than the others).
  const tabBody = 'h-[34rem] overflow-y-auto pr-1';

  const infoSubmitting = create.isPending || update.isPending;
  const infoSubmitLabel = !current
    ? infoSubmitting
      ? 'Saving…'
      : 'Save & continue →'
    : infoSubmitting
      ? 'Saving…'
      : 'Save';

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? virtualCatalog
                ? 'New virtual product'
                : 'New product'
              : `Edit ${product?.name ?? 'product'}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' && !current
              ? 'Enter the product details to create it, then add images, discounts and more.'
              : 'Each tab saves independently.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="flex-wrap">
            <TabsTrigger value="info">Product info</TabsTrigger>
            <TabsTrigger value="media" disabled={!current}>
              Images &amp; Inclusions
            </TabsTrigger>
            <TabsTrigger value="discount" disabled={!current}>
              Discount
            </TabsTrigger>
            <TabsTrigger value="requirements" disabled={!current}>
              Prerequisite
            </TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
            {current && <TabsTrigger value="testimonials">Testimonials</TabsTrigger>}
            {current && !current.isKit && !current.isBundle && (
              <TabsTrigger value="variations">Variations</TabsTrigger>
            )}
            {/* Suppliers are a purchasing/restock concern — a service is never
                received into inventory, so the tab is noise on a virtual product. */}
            {current && !virtualCatalog && <TabsTrigger value="suppliers">Suppliers</TabsTrigger>}
            {current && showUnilevel && (
              <TabsTrigger value="unilevel">Realtime Unilevel</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="info" className={tabBody}>
            <ProductDetailsForm
              product={current ?? undefined}
              memberships={memberships}
              categories={categories}
              detailsHtml={detailsHtml}
              virtualCatalog={virtualCatalog}
              virtualFeatureEnabled={virtualFeatureEnabled}
              submitLabel={infoSubmitLabel}
              submitting={infoSubmitting}
              saved={(create.isSuccess || update.isSuccess) && !infoSubmitting}
              error={create.error ?? (skuRenameHandled ? undefined : update.error)}
              // Edit mode ALWAYS updates; create mode creates first, then updates
              // the just-created row. Never key this on object truthiness — a
              // missing `current` in edit mode used to fire a duplicate-SKU CREATE.
              onSubmit={(d) => (mode === 'edit' || current ? update.mutate(d) : create.mutate(d))}
              onCancel={onClose}
            />
          </TabsContent>

          <TabsContent value="media" className={`${tabBody} space-y-5`}>
            {current && (
              <>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Images</h3>
                  <ImagesSection productId={current.id} initial={current.images ?? []} />
                </div>
                {(current.isKit || current.isBundle) && (
                  <div className="border-t pt-3">
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      {current.isBundle
                        ? 'Bundle contents (inclusive items)'
                        : 'Kit contents (inclusive items)'}
                    </h3>
                    {current.isBundle && (
                      <p className="mb-2 text-xs text-muted-foreground">
                        A bundle has no stock of its own at a company branch — selling it deducts
                        these items’ inventory. It may include another bundle (a box of 10 packs). A
                        stockist holding it opens it into these items when a sale needs them. Add at
                        least one item or the bundle can’t be sold.
                      </p>
                    )}
                    <KitComponentsSection kitId={current.id} isBundle={!!current.isBundle} />
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="discount" className={tabBody}>
            {current && (
              <PerProductPricingEditor productId={current.id} basePrice={current.price} />
            )}
          </TabsContent>

          <TabsContent value="requirements" className={tabBody}>
            {current && (
              <MembershipRequirementsSection productId={current.id} memberships={memberships} />
            )}
          </TabsContent>

          <TabsContent value="details" className={`${tabBody} space-y-3`}>
            <p className="text-sm text-muted-foreground">
              Rich product description shown on the storefront product page. Use the toolbar to
              format — no code needed.
            </p>
            <RichTextEditor
              value={detailsHtml}
              onChange={setDetailsHtml}
              placeholder="Describe the product — ingredients, usage, what's in the box…"
            />
            {current ? (
              <div className="flex items-center justify-end gap-3">
                {saveDetails.isSuccess && <span className="text-xs text-emerald-600">Saved.</span>}
                <Button
                  type="button"
                  onClick={() => saveDetails.mutate()}
                  disabled={saveDetails.isPending}
                >
                  {saveDetails.isPending ? 'Saving…' : 'Save details'}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Saved together when you create the product.
              </p>
            )}
          </TabsContent>

          {current && (
            <TabsContent value="testimonials" className={tabBody}>
              <ProductTestimonialsSection productId={current.id} />
            </TabsContent>
          )}

          {current && !current.isKit && !current.isBundle && (
            <TabsContent value="variations" className={tabBody}>
              <VariantsSection productId={current.id} product={current} />
            </TabsContent>
          )}

          {current && (
            <TabsContent value="suppliers" className={tabBody}>
              <ProductSuppliersSection productId={current.id} />
            </TabsContent>
          )}

          {current && showUnilevel && (
            <TabsContent value="unilevel" className={tabBody}>
              <ProductRealtimeUnilevelEditor productId={current.id} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>

      {skuRenameConfirm && (
        <SkuRenameConfirmDialog
          usage={skuRenameConfirm.usage}
          oldSku={current?.sku ?? ''}
          newSku={skuRenameConfirm.details.sku}
          submitting={update.isPending}
          onCancel={() => {
            // Clear the mutation too, not just the dialog state — a lingering
            // `update.error` reappears as red text the moment the dialog closes.
            update.reset();
            setSkuRenameConfirm(null);
          }}
          onConfirm={() => update.mutate({ ...skuRenameConfirm.details, confirmSkuRename: true })}
        />
      )}
    </Dialog>
  );
}

/**
 * Confirms a SKU rename on a product that's already referenced. The rename is
 * data-safe — orders, stock movements and POs all point at the product id, never
 * the code — so this warns about the physical world (printed barcodes, shelf
 * tags, a supplier's catalog) rather than blocking.
 *
 * The title deliberately does NOT say "already in use". The server code behind
 * this dialog is `sku_in_use`, but that is about the product having HISTORY, not
 * about the new code colliding with another product (that is `sku_taken`, which
 * hard-rejects and never gets here). Admins read "already in use" as a
 * collision, hit Cancel, and go hunting for a duplicate that doesn't exist.
 */
function SkuRenameConfirmDialog({
  usage,
  oldSku,
  newSku,
  submitting,
  onCancel,
  onConfirm,
}: {
  usage?: SkuUsage;
  oldSku: string;
  newSku: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // `variants` is returned by countSkuUsage but is NOT part of the server's
  // `referenced` predicate — listing it would explain a warning variants didn't
  // cause.
  const refs = [
    { label: 'order lines', n: usage?.orderLines ?? 0 },
    { label: 'stock movements', n: usage?.inventoryMovements ?? 0 },
    { label: 'purchase-order lines', n: usage?.purchaseOrderLines ?? 0 },
  ].filter((r) => r.n > 0);
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change this product&rsquo;s SKU?</DialogTitle>
          <DialogDescription>
            This won&apos;t break any records — orders, stock and purchase orders reference the
            product itself, not its code. But anything printed with the old code (barcodes, shelf
            tags, a supplier catalog) will stop scanning.
          </DialogDescription>
        </DialogHeader>
        {/* The admin can't otherwise see what they're changing FROM — the form
            field already holds the new value by the time this opens. */}
        <p className="font-mono text-sm">
          <span className="text-muted-foreground line-through">{oldSku}</span>
          <span className="mx-2 text-muted-foreground">&rarr;</span>
          <span className="font-semibold">{newSku}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          {refs.length > 0
            ? `Currently referenced by ${refs.map((r) => `${r.n} ${r.label}`).join(', ')}.`
            : 'This product already has activity in the system.'}
        </p>
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Keep {oldSku}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={submitting}>
            {submitting ? 'Saving…' : 'Change SKU'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductDetailsForm({
  product,
  memberships,
  categories,
  detailsHtml,
  submitLabel,
  submitting,
  saved,
  virtualCatalog,
  virtualFeatureEnabled,
  onSubmit,
  onCancel,
  error,
}: {
  product?: Product;
  memberships: Membership[];
  categories: Category[];
  /**
   * Rendered inside the Virtual products page: `isVirtual` is implied by the
   * page itself, so the flag is forced on and its checkbox hidden (toggling it
   * here would move the product to the OTHER catalog, which reads as the row
   * vanishing). Shipping-only controls go with it.
   */
  virtualCatalog?: boolean;
  /** Tenant master switch — off hides the "Virtual product / service" flag entirely. */
  virtualFeatureEnabled?: boolean;
  // Rich Details HTML is owned by the parent dialog (edited in the Details tab);
  // the info form just forwards the current value into its create/update payload.
  detailsHtml: string;
  submitLabel: string;
  submitting: boolean;
  saved?: boolean;
  onSubmit: (details: ProductDetails) => void;
  onCancel: () => void;
  error: unknown;
}) {
  const [sku, setSku] = useState(product?.sku ?? '');
  // On the create form the SKU auto-fills from the name (slug/uppercase) until
  // the admin edits it themselves. On edit it stays editable — a typo'd SKU used
  // to be permanent (archive + recreate was the only fix).
  const [skuTouched, setSkuTouched] = useState(false);
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [videoUrl, setVideoUrl] = useState(product?.videoUrl ?? '');
  const [price, setPrice] = useState(product?.price ?? '');
  const [pv, setPv] = useState(product?.pv ?? '');
  // Accounting Phase 1: unit cost (COGS basis). Blank = unknown → null.
  const [cost, setCost] = useState(product?.cost ?? '');
  // Accounting Phase 2: tax classification (3-code registry; default vatable).
  const [taxCode, setTaxCode] = useState<'vatable' | 'exempt' | 'zero_rated'>(
    product?.taxCode ?? 'vatable',
  );
  const [priceGc, setPriceGc] = useState(product?.priceGc ?? '');
  const [priceCoin, setPriceCoin] = useState(product?.priceCoin ?? '');
  // "Sold in this currency" is its own flag, not inferred from the price being
  // set — a 0 in a price field is not a way to say "not sold".
  const [gcPurchasable, setGcPurchasable] = useState(product?.gcPurchasable ?? false);
  const [coinPurchasable, setCoinPurchasable] = useState(product?.coinPurchasable ?? false);
  const [coinGrant, setCoinGrant] = useState(product?.coinGrant ?? '0');
  // Whole answers; empty means the product grants none.
  const [aiCreditReward, setAiCreditReward] = useState(
    product?.aiCreditReward != null ? String(product.aiCreditReward) : '',
  );
  const [binaryPoints, setBinaryPoints] = useState(product?.binaryPoints ?? '0');
  const [vortexToken, setVortexToken] = useState(product?.vortexToken ?? '0');
  const [weightKg, setWeightKg] = useState(product?.weightKg ?? '');
  const [lengthCm, setLengthCm] = useState(product?.lengthCm ?? '');
  const [widthCm, setWidthCm] = useState(product?.widthCm ?? '');
  const [heightCm, setHeightCm] = useState(product?.heightCm ?? '');
  const [categoryId, setCategoryId] = useState<string | null>(product?.categoryId ?? null);
  const [isKit, setIsKit] = useState(product?.isKit ?? false);
  const [kitMembershipId, setKitMembershipId] = useState<string | null>(
    product?.kitMembershipId ?? null,
  );
  const [kitSlotType, setKitSlotType] = useState<'paid' | 'free' | 'cd' | 'half_cd'>(
    product?.kitSlotType ?? 'paid',
  );
  const [kitUsageMode, setKitUsageMode] = useState<'activation' | 'upgrade' | 'both'>(
    product?.kitUsageMode ?? 'activation',
  );
  const [kitDebtAmount, setKitDebtAmount] = useState(product?.kitDebtAmount ?? '');
  const isCdKit = kitSlotType === 'cd' || kitSlotType === 'half_cd';
  const [featured, setFeatured] = useState(product?.featured ?? false);
  const [redeemOnly, setRedeemOnly] = useState(product?.redeemOnly ?? false);
  // A redeem-only product is by definition sold in GC, so the toggle is forced
  // on and locked. Deriving it (rather than only setting state on the checkbox)
  // also repairs pre-existing redeem products, whose column defaulted to false.
  const gcSold = redeemOnly || gcPurchasable;
  const [isBundle, setIsBundle] = useState(product?.isBundle ?? false);
  const [freeShipping, setFreeShipping] = useState(product?.freeShipping ?? false);
  const [isVirtual, setIsVirtual] = useState(product?.isVirtual ?? !!virtualCatalog);
  const [replicatedPageId, setReplicatedPageId] = useState<string | null>(
    product?.replicatedPageId ?? null,
  );
  const [platformSold, setPlatformSold] = useState<'cashier' | 'ecom' | 'both'>(
    product?.platformSold ?? 'both',
  );
  const [soldTo, setSoldTo] = useState<'activated' | 'non_activated' | 'both'>(
    product?.soldTo ?? 'both',
  );
  const [localError, setLocalError] = useState<string | null>(null);

  // Accounting Phase 1: the cost field + suggestion chip only exist for
  // accounting tenants (same shared cache key as every other feature gate).
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const accountingEnabled = featuresQuery.data?.accounting_enabled === true;
  const costSuggestionQuery = useQuery({
    queryKey: ['cost-suggestion', product?.id],
    queryFn: () => getProductCostSuggestion(product!.id),
    enabled: accountingEnabled && !!product,
    staleTime: 60_000,
  });
  const costSuggestion = costSuggestionQuery.data ?? null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    // A virtual product / service has nothing to weigh or measure. This gate
    // runs ABOVE the kit checks, so skipping it here is also what lets a
    // virtual membership kit be saved.
    if (!isVirtual) {
      if (!weightKg.trim() || Number(weightKg) <= 0) {
        setLocalError('Weight (g) is required and must be greater than 0.');
        return;
      }
      const dims: Array<[string, string]> = [
        ['Length', lengthCm],
        ['Width', widthCm],
        ['Height', heightCm],
      ];
      for (const [label, val] of dims) {
        if (!val.trim() || Number(val) <= 0) {
          setLocalError(`${label} (cm) is required and must be greater than 0.`);
          return;
        }
      }
    }
    if (isKit && !kitMembershipId) {
      setLocalError('Pick a membership tier for the kit.');
      return;
    }
    if (isKit && isCdKit && (!kitDebtAmount.trim() || Number(kitDebtAmount) <= 0)) {
      setLocalError('A CD / Half-CD kit needs a debt amount greater than 0.');
      return;
    }
    if (redeemOnly && !isPositiveMoney(priceGc)) {
      setLocalError('A redeem product needs a GC price greater than 0.');
      return;
    }
    // A currency the product IS sold in must carry a real price. Zero is not a
    // way to opt out — the checkout gate would charge the line as free.
    if (gcSold && !isPositiveMoney(priceGc)) {
      setLocalError('“Sell in GC” is on — the GC price must be greater than 0.');
      return;
    }
    if (coinPurchasable && !isPositiveMoney(priceCoin)) {
      setLocalError('“Sell in Coin” is on — the Coin price must be greater than 0.');
      return;
    }
    // The server caps these four. Without a check here the only feedback is the
    // raw zod sentence ("String must contain at most 2000 character(s)"), which
    // an operator reads as "save failed" — the create form is worst hit, since
    // it posts the Details HTML together with the info tab, so a long paste in
    // EITHER field kills the save with no hint which one. Name the field, the
    // count and the limit instead. `maxLength` on the plain inputs stops most of
    // this at the keystroke; the SKU is derived from the name and the Details
    // body comes from the rich editor, so both still need the check.
    const lengthCaps: Array<[string, string, number]> = [
      ['SKU', sku, 64],
      ['Name', name, 200],
      ['Description', description, 2000],
      // Counted as HTML, formatting included — pasting styled text or an
      // inline image blows past this far sooner than the visible text suggests.
      ['Details', detailsHtml, 20000],
    ];
    for (const [label, value, max] of lengthCaps) {
      if (value.length > max) {
        setLocalError(
          `${label} is too long — ${value.length.toLocaleString()} characters, and the limit is ${max.toLocaleString()}. Shorten it and save again.`,
        );
        return;
      }
    }
    setLocalError(null);
    onSubmit({
      sku,
      name,
      description: description || undefined,
      detailsHtml: detailsHtml.trim() === '' ? null : detailsHtml,
      videoUrl: videoUrl.trim() === '' ? null : videoUrl.trim(),
      price: price.trim(),
      // Redeem items carry no PV (the backend also forces this).
      pv: redeemOnly ? '0' : pv.trim(),
      // Omit entirely when the accounting flag is off (the field isn't shown),
      // so a PATCH never clears a stored cost the admin couldn't see.
      cost: accountingEnabled ? (cost.trim() === '' ? null : cost.trim()) : undefined,
      // Same omit-when-hidden rule as `cost`.
      taxCode: accountingEnabled ? taxCode : undefined,
      // Toggle off ⇒ clear the stored price outright. Keeping a stale amount on
      // a currency the product isn't sold in is what the checkout gate had to
      // guess about in the first place.
      gcPurchasable: gcSold,
      coinPurchasable,
      priceGc: gcSold ? priceGc.trim() : null,
      priceCoin: coinPurchasable ? priceCoin.trim() : null,
      coinGrant: coinGrant.trim() || '0',
      // Empty = no reward (NULL); the CHECK rejects 0, so don't coerce to one.
      aiCreditReward: aiCreditReward.trim() ? Math.floor(Number(aiCreditReward)) : null,
      // A membership kit has no binary points (a repurchase mechanic).
      binaryPoints: isKit ? '0' : binaryPoints.trim() || '0',
      vortexToken: vortexToken.trim() || '0',
      // A virtual product carries no dimensions. Zero them explicitly rather
      // than leaving a stale 5kg on a row now flagged "no shipping" — harmless
      // either way (computeParcel skips virtual lines) but a landmine to read.
      weightKg: isVirtual ? '0' : weightKg.trim(),
      lengthCm: isVirtual ? '0' : lengthCm.trim(),
      widthCm: isVirtual ? '0' : widthCm.trim(),
      heightCm: isVirtual ? '0' : heightCm.trim(),
      categoryId,
      // Kit, redeem, and bundle are mutually exclusive.
      isKit: redeemOnly || isBundle ? false : isKit,
      kitMembershipId: !redeemOnly && !isBundle && isKit ? kitMembershipId : null,
      // CD/Half-CD kits are activation-only (server-enforced too).
      kitSlotType: !redeemOnly && !isBundle && isKit ? kitSlotType : undefined,
      kitUsageMode:
        !redeemOnly && !isBundle && isKit ? (isCdKit ? 'activation' : kitUsageMode) : undefined,
      kitDebtAmount: !redeemOnly && !isBundle && isKit && isCdKit ? kitDebtAmount.trim() : null,
      platformSold,
      soldTo,
      featured,
      redeemOnly: isBundle ? false : redeemOnly,
      isBundle,
      freeShipping,
      isVirtual,
      replicatedPageId: isVirtual ? replicatedPageId : null,
    });
  }

  const errMsg = localError ?? (error ? apiErrorMessage(error, 'Save failed') : undefined);

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Name" htmlFor="name">
        <Input
          id="name"
          value={name}
          onChange={(e) => {
            const next = e.target.value;
            setName(next);
            // Auto-derive the SKU from the name on creation until the admin
            // edits the SKU directly. Clamped to the server's 64-char cap — a
            // long product name would otherwise mint a SKU the save rejects,
            // pointing the error at a field the operator never typed in.
            if (!product && !skuTouched) setSku(skuFromName(next).slice(0, 64));
          }}
          maxLength={200}
          required
        />
      </Field>
      <Field label="SKU" htmlFor="sku">
        <Input
          id="sku"
          value={sku}
          onChange={(e) => {
            setSkuTouched(true);
            setSku(e.target.value.toUpperCase());
          }}
          maxLength={64}
          required
        />
        {/* Compare NORMALIZED, mirroring the server: the field is seeded verbatim
            from the row but uppercased on keystroke, so a product stored with a
            non-uppercase sku (archived `-Archived`, legacy-import `LEG-*`) would
            otherwise show this warning on an edit the server correctly ignores. */}
        {product && normalizeSku(sku) !== normalizeSku(product.sku) && (
          <p className="mt-1 text-xs text-amber-600">
            Changing a live SKU invalidates printed barcodes and price tags carrying the old code.
          </p>
        )}
      </Field>

      {/* Kit flag sits with identity, before the points/pricing fields. */}
      <div className="md:col-span-2 flex items-center gap-4 flex-wrap rounded-md border bg-background/60 p-2">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={isKit}
            disabled={redeemOnly || isBundle}
            onCheckedChange={(v) => setIsKit(v === true)}
          />
          This is a membership kit
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={redeemOnly}
            disabled={isKit || isBundle}
            onCheckedChange={(v) => {
              const on = v === true;
              setRedeemOnly(on);
              // A redeem product is only ever sold in GC — persist that intent
              // so unchecking Redeem-only later doesn't silently un-sell it.
              if (on) {
                setIsKit(false);
                setGcPurchasable(true);
              }
            }}
          />
          Redeem-only (GC, no PV)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={isBundle}
            disabled={isKit || redeemOnly}
            onCheckedChange={(v) => {
              const on = v === true;
              setIsBundle(on);
              if (on) {
                setIsKit(false);
                setRedeemOnly(false);
              }
            }}
          />
          Promo bundle (no own stock)
        </label>
        {/* Virtual sits with the other identity flags, ABOVE the shipping
            fields it governs — an admin creating a service shouldn't have to
            fill in four "required" dimensions before finding the switch that
            removes them. Deliberately NOT exclusive with kit/redeem/bundle:
            a virtual membership kit is a supported combination. */}
        {/* Hidden on the Virtual products page (the flag IS the page — a toggle
            there would silently move the row to the other catalog, which reads
            as it disappearing) and hidden entirely while the tenant has virtual
            products turned off. An already-flagged product keeps its behaviour
            either way; only the control goes. */}
        {!virtualCatalog && virtualFeatureEnabled && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={isVirtual}
              onCheckedChange={(v) => {
                const on = v === true;
                // Turning Virtual OFF clears the replicated page on save
                // (`replicatedPageId: isVirtual ? replicatedPageId : null`) and
                // re-ticking it does NOT bring the assignment back. Silent data
                // loss on a checkbox — say so before it happens.
                if (!on && replicatedPageId) {
                  const ok = window.confirm(
                    'This product opens a campaign page. Turning off “Virtual product / service” clears that link when you save, and re-ticking it later won’t restore it. Continue?',
                  );
                  if (!ok) return;
                }
                setIsVirtual(on);
              }}
            />
            Virtual product / service
          </label>
        )}
        {isVirtual && !virtualCatalog && virtualFeatureEnabled && (
          <span className="text-xs text-muted-foreground">
            No shipping and no weight / dimensions. An order of only virtual items charges no
            shipping fee and needs no courier; mixed orders still ship the physical items. Not
            inventory-tracked.
          </span>
        )}
        {isBundle && (
          <span className="text-xs text-muted-foreground">
            Sold like a normal product but holds no stock — each sale deducts its inclusive items’
            inventory. Add the items on the Images &amp; Inclusions tab. House-only; no variations.
          </span>
        )}
        {redeemOnly && (
          <span className="text-xs text-muted-foreground">
            Sold only in the GC Redeem section. Requires a GC price; PV is forced to 0 and the cash
            price is ignored.
          </span>
        )}
        {isKit && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Tier:</span>
            <Select
              value={kitMembershipId ?? '__none__'}
              onValueChange={(v) => setKitMembershipId(v === '__none__' ? null : v)}
            >
              <SelectTrigger className="h-9 w-48">
                <SelectValue placeholder="Select tier…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select tier…</SelectItem>
                {memberships.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">Slot:</span>
            <Select
              value={kitSlotType}
              onValueChange={(v) => {
                const next = v as 'paid' | 'free' | 'cd' | 'half_cd';
                setKitSlotType(next);
                // CD/Half-CD kits are activation-only.
                if (next === 'cd' || next === 'half_cd') setKitUsageMode('activation');
              }}
            >
              <SelectTrigger className="h-9 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="cd">CD — Commission Deductible</SelectItem>
                <SelectItem value="half_cd">Half-CD</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">Use:</span>
            <Select
              value={kitUsageMode}
              onValueChange={(v) => setKitUsageMode(v as 'activation' | 'upgrade' | 'both')}
              disabled={isCdKit}
            >
              <SelectTrigger
                className="h-9 w-36"
                title={isCdKit ? 'CD / Half-CD kits are activation-only' : undefined}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activation">Activation</SelectItem>
                <SelectItem value="upgrade">Upgrade</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
            {isCdKit && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Debt:</span>
                <MoneyInput
                  className="h-9 w-32"
                  value={kitDebtAmount}
                  onChange={(e) => setKitDebtAmount(e.target.value)}
                  placeholder="0.00"
                  title="The debt the minted slot starts with — its earnings pay this off before reaching the wallet"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sale-channel + buyer-eligibility policy. */}
      <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-md border bg-background/60 p-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sold on:</span>
          <Select
            value={platformSold}
            onValueChange={(v) => setPlatformSold(v as typeof platformSold)}
          >
            <SelectTrigger className="h-9 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Both</SelectItem>
              <SelectItem value="ecom">E-commerce only</SelectItem>
              <SelectItem value="cashier">Cashier only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sold to:</span>
          <Select value={soldTo} onValueChange={(v) => setSoldTo(v as typeof soldTo)}>
            <SelectTrigger className="h-9 w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Everyone</SelectItem>
              <SelectItem value="activated">Activated members only</SelectItem>
              <SelectItem value="non_activated">Non-activated members only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Field label="Price" htmlFor="price">
        <MoneyInput
          id="price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="0.00"
          required
        />
      </Field>
      {/* Accounting Phase 1: internal unit cost — feeds the checkout COGS
          snapshot, margin columns and the at-cost valuation. Never buyer-facing. */}
      {accountingEnabled && (
        <Field label="Unit cost (internal)" htmlFor="cost">
          <MoneyInput
            id="cost"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="unknown"
          />
          {costSuggestion &&
            (() => {
              // One derivation for label AND fill, so what the chip offers is
              // exactly what clicking it puts in the field.
              const suggested = new Decimal(costSuggestion.unitCost).toString();
              return (
                <button
                  type="button"
                  className="mt-1 inline-flex items-center rounded-full border bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent"
                  onClick={() => setCost(suggested)}
                  title={
                    costSuggestion.source === 'purchase_order'
                      ? `Latest approved PO line (${costSuggestion.reference})`
                      : `Preferred supplier (${costSuggestion.reference})`
                  }
                >
                  Suggested: {suggested} · {costSuggestion.reference}
                </button>
              );
            })()}
        </Field>
      )}
      {/* Accounting Phase 2: 3-code tax registry. Classify the catalog BEFORE
          flipping "House VAT on orders" so the first post-flip checkouts
          snapshot correctly — hence the accounting gate, not the VAT gate. */}
      {accountingEnabled && (
        <Field label="Tax classification" htmlFor="tax-code">
          <Select
            value={taxCode}
            onValueChange={(v) => setTaxCode(v as 'vatable' | 'exempt' | 'zero_rated')}
          >
            <SelectTrigger id="tax-code" className="h-9 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vatable">VATable</SelectItem>
              <SelectItem value="exempt">VAT-exempt</SelectItem>
              <SelectItem value="zero_rated">Zero-rated</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
      <Field label={redeemOnly ? 'PV (none for redeem)' : 'PV'} htmlFor="pv">
        <MoneyInput
          id="pv"
          value={redeemOnly ? '0' : pv}
          onChange={(e) => setPv(e.target.value)}
          placeholder="0.00"
          disabled={redeemOnly}
          required={!redeemOnly}
        />
      </Field>
      {/* Binary points only apply to non-kit (repurchase) products. */}
      {!isKit && (
        <Field label="Binary points" htmlFor="binaryPoints">
          <MoneyInput
            id="binaryPoints"
            value={binaryPoints}
            onChange={(e) => setBinaryPoints(e.target.value)}
            placeholder="0.00"
          />
        </Field>
      )}
      <Field label="Vortex tokens" htmlFor="vortexToken">
        <MoneyInput
          id="vortexToken"
          value={vortexToken}
          onChange={(e) => setVortexToken(e.target.value)}
          placeholder="0.00"
        />
      </Field>
      {/* Wallet-currency sale intent. The checkbox — not a blank or zero price —
          is what says "sold in this currency": a stored 0 once slipped past the
          null-only checkout gate and sold a kit for nothing. Prices stay visible
          (greyed) when a toggle is off so a typed amount isn't lost on a
          mis-click; the payload clears them anyway. */}
      <div className="md:col-span-2 space-y-2 rounded-md border bg-background/60 p-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Purchasable with wallet currencies
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className={`space-y-1 ${gcSold ? '' : 'opacity-50'}`}>
            {/* The title sits on the label, not the Checkbox: a disabled button
                swallows the hover events a tooltip needs. */}
            <label
              className="flex items-center gap-2 text-sm"
              title={redeemOnly ? 'A redeem-only product is always sold in GC' : undefined}
            >
              <Checkbox
                checked={gcSold}
                disabled={redeemOnly}
                onCheckedChange={(v) => setGcPurchasable(v === true)}
              />
              Sell in GC
            </label>
            <Field label="GC price" htmlFor="priceGc">
              <MoneyInput
                id="priceGc"
                value={priceGc ?? ''}
                onChange={(e) => setPriceGc(e.target.value)}
                placeholder="0.00"
                required={gcSold}
                disabled={!gcSold}
              />
            </Field>
            <p className="text-[11px] text-muted-foreground">Must be greater than 0.</p>
          </div>
          <div className={`space-y-1 ${coinPurchasable ? '' : 'opacity-50'}`}>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={coinPurchasable}
                onCheckedChange={(v) => setCoinPurchasable(v === true)}
              />
              Sell in Coin
            </label>
            <Field label="Coin price" htmlFor="priceCoin">
              <MoneyInput
                id="priceCoin"
                value={priceCoin ?? ''}
                onChange={(e) => setPriceCoin(e.target.value)}
                placeholder="0.00"
                required={coinPurchasable}
                disabled={!coinPurchasable}
              />
            </Field>
            <p className="text-[11px] text-muted-foreground">Must be greater than 0.</p>
          </div>
        </div>
      </div>
      <Field label="Coin grant (per unit, cash purchase)" htmlFor="coinGrant">
        <MoneyInput
          id="coinGrant"
          value={coinGrant}
          onChange={(e) => setCoinGrant(e.target.value)}
          placeholder="0.00"
        />
      </Field>
      <Field label="AI answers (per unit)" htmlFor="aiCreditReward">
        <Input
          id="aiCreditReward"
          type="number"
          min={0}
          step={1}
          value={aiCreditReward}
          onChange={(e) => setAiCreditReward(e.target.value)}
          placeholder="0"
        />
      </Field>
      <Field label="Category" htmlFor="categoryId">
        <Select
          value={categoryId ?? '__none__'}
          onValueChange={(v) => setCategoryId(v === '__none__' ? null : v)}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Uncategorized" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Uncategorized</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {/* Shipping: weight sits with the dimensions — they belong together.
          Column is named weightKg for legacy reasons but now holds grams.
          A virtual product / service has nothing to weigh or measure, so the
          whole block is gone rather than dimmed — the toggle up in the flags
          strip already says why. `submit()` zeroes the stored values, so a
          product switched to virtual can't keep stale dimensions. */}
      {!isVirtual && (
        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Weight (g) — required" htmlFor="weightKg">
            <MoneyInput
              id="weightKg"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              placeholder="0"
              required
            />
          </Field>
          <Field label="Length (cm) — required" htmlFor="lengthCm">
            <MoneyInput
              id="lengthCm"
              value={lengthCm}
              onChange={(e) => setLengthCm(e.target.value)}
              placeholder="0"
              required
            />
          </Field>
          <Field label="Width (cm) — required" htmlFor="widthCm">
            <MoneyInput
              id="widthCm"
              value={widthCm}
              onChange={(e) => setWidthCm(e.target.value)}
              placeholder="0"
              required
            />
          </Field>
          <Field label="Height (cm) — required" htmlFor="heightCm">
            <MoneyInput
              id="heightCm"
              value={heightCm}
              onChange={(e) => setHeightCm(e.target.value)}
              placeholder="0"
              required
            />
          </Field>
        </div>
      )}
      <div className="md:col-span-2">
        <Field label="Description" htmlFor="description">
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            className="min-h-16"
          />
        </Field>
      </div>
      <div className="md:col-span-2">
        <Field label="YouTube video URL (optional)" htmlFor="videoUrl">
          <Input
            id="videoUrl"
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Shown as an embedded video above the product description for buyers.
          </p>
        </Field>
      </div>
      <label className="md:col-span-2 flex items-center gap-2 text-sm">
        <Checkbox checked={featured} onCheckedChange={(v) => setFeatured(v === true)} />
        <span>
          Featured
          <span className="ml-1 text-xs text-muted-foreground">
            — sorts the product first in Shop and category listings, and breaks ties in search.
            Tenant Settings → Storefront home can additionally leave Featured products out of the
            storefront home&apos;s product grid.
          </span>
        </span>
      </label>
      {/* A virtual product never carries a shipping fee in the first place, so
          "free shipping" is a meaningless promise on one. */}
      {!isVirtual && (
        <label className="md:col-span-2 flex items-center gap-2 text-sm">
          <Checkbox checked={freeShipping} onCheckedChange={(v) => setFreeShipping(v === true)} />
          <span>
            Free shipping
            <span className="ml-1 text-xs text-muted-foreground">
              — no shipping fee when a store's cart is all free-shipping items; shows a Free
              Shipping badge.
            </span>
          </span>
        </label>
      )}
      {/* The Virtual toggle itself lives up in the flags strip; only the
          campaign-page picker stays here — it's full-width and would crowd it. */}
      {isVirtual && <ReplicatedPageField value={replicatedPageId} onChange={setReplicatedPageId} />}

      {errMsg && <p className="md:col-span-2 text-sm text-destructive">{errMsg}</p>}
      <div className="md:col-span-2 flex items-center gap-2 pt-2">
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {saved && !errMsg ? <span className="text-sm text-emerald-600">Saved.</span> : null}
      </div>
    </form>
  );
}

function ImagesSection({ productId, initial }: { productId: string; initial: string[] }) {
  const [images, setImages] = useState<string[]>(initial);
  const upload = useMutation({
    mutationFn: (file: File) => uploadProductImage(productId, file),
    meta: { successMessage: 'Image uploaded.' },
    onSuccess: (r) => setImages(r.images),
  });
  const remove = useMutation({
    mutationFn: (key: string) => deleteProductImage(productId, key),
    meta: { successMessage: 'Image removed.' },
    onSuccess: (r) => setImages(r.images),
  });
  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {images.map((key, idx) => (
          <div key={key} className="relative h-20 w-20 rounded-md border overflow-hidden group">
            <img
              src={`${productImageUrl(productId, idx)}?v=${encodeURIComponent(key)}`}
              alt=""
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => remove.mutate(key)}
              className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-background/80 text-destructive text-xs leading-none hover:bg-background"
              title="Remove"
            >
              ✕
            </button>
          </div>
        ))}
        <label className="h-20 w-20 rounded-md border border-dashed flex items-center justify-center text-xs text-muted-foreground cursor-pointer hover:bg-accent">
          {upload.isPending ? 'Uploading…' : '+ Image'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {(upload.error || remove.error) && (
        <p className="text-xs text-destructive">Image operation failed (check file type/size).</p>
      )}
    </div>
  );
}

/**
 * "Replicated page" picker for a VIRTUAL product — where a buyer lands when they
 * click this product in the store, instead of the normal product page. Only
 * takes effect while `virtual_products_open_replicated_page` is on (Settings →
 * Pricing & fulfillment); the copy says so, since the assignment is otherwise
 * silently inert. Rendered only when the product is virtual, and the payload
 * clears it when the virtual flag is turned off.
 */
function ReplicatedPageField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const pagesQ = useQuery({ queryKey: ['admin-replicated-pages'], queryFn: listReplicatedPages });
  const pages = pagesQ.data?.pages ?? [];
  return (
    <div className="md:col-span-2 space-y-1">
      <label className="text-sm font-medium">Replicated page</label>
      <Select value={value ?? 'none'} onValueChange={(v) => onChange(v === 'none' ? null : v)}>
        <SelectTrigger className="h-9">
          <SelectValue placeholder="— None —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— None (opens the product page) —</SelectItem>
          {pages.map((pg) => (
            <SelectItem key={pg.id} value={pg.id}>
              {pg.title} /{pg.slug}
              {pg.enabled ? '' : ' (disabled)'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Where a buyer lands when they click this product in the store. Needs{' '}
        <b>Virtual products open their replicated page</b> (Settings → Pricing &amp; fulfillment)
        turned on; a disabled or deleted page falls back to the product page.
        {pages.length === 0 && ' No replicated pages yet — create one under Replicated Pages.'}
      </p>
      {/*
        This pointer is one-way on purpose. A page may front several products,
        so it cannot infer which one it sells from this field — and inferring it
        is how a campaign page once silently became every member's landing page.
        The page names its own product, explicitly, on its own screen.
      */}
      {value && (
        <p className="text-xs text-muted-foreground">
          This only routes the store click. For the page&apos;s own button to sell this product to
          someone who arrives from a shared referral link, set <b>Sells</b> on that page under
          Replicated Pages — otherwise the button just registers them.
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * Typed editor for a kit's inclusive items: a table of (component product, qty
 * per kit) rows. Self-contained — loads + saves its own rows via the
 * components endpoint. NOT a raw-JSON editor.
 */
/**
 * Package prerequisite editor: pick the membership tier(s) a buyer's active slot
 * must hold for this product to be visible/sellable. None checked = available to
 * everyone (the default). Persisted via PUT /products/:id/membership-requirements.
 */
function MembershipRequirementsSection({
  productId,
  memberships,
}: {
  productId: string;
  memberships: Membership[];
}) {
  const qc = useQueryClient();
  const reqQuery = useQuery({
    queryKey: ['product-requirements', productId],
    queryFn: () => getProductMembershipRequirements(productId),
  });
  const [selected, setSelected] = useState<string[] | null>(null);
  const current = selected ?? reqQuery.data ?? [];

  const save = useMutation({
    mutationFn: () => saveProductMembershipRequirements(productId, current),
    meta: { successMessage: 'Prerequisite saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-requirements', productId] }),
  });

  function toggle(id: string, on: boolean) {
    setSelected(on ? [...new Set([...current, id])] : current.filter((m) => m !== id));
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Restrict this product to buyers who hold an active slot in one of the selected membership
        tiers. Leave everything unchecked to make it available to everyone. Any of the buyer&rsquo;s
        active slots qualifies.
      </p>
      {reqQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : memberships.length === 0 ? (
        <p className="text-sm text-muted-foreground">No membership tiers defined.</p>
      ) : (
        <div className="space-y-1">
          {memberships.map((m) => (
            <label key={m.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={current.includes(m.id)}
                onCheckedChange={(v) => toggle(m.id, v === true)}
              />
              {m.name} <span className="text-muted-foreground">({m.code})</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex items-center justify-end">
        <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save prerequisite'}
        </Button>
      </div>
    </div>
  );
}

function KitComponentsSection({ kitId, isBundle }: { kitId: string; isBundle: boolean }) {
  const qc = useQueryClient();
  const componentsQuery = useQuery({
    queryKey: ['kit-components', kitId],
    queryFn: () => getKitComponents(kitId),
  });
  const [rows, setRows] = useState<ComponentDraft[] | null>(null);
  const current =
    rows ??
    (componentsQuery.data ?? []).map((c) => ({
      componentProductId: c.componentProductId,
      variantId: c.variantId,
      qtyPerKit: c.qtyPerKit,
      sku: c.sku,
      name: c.name,
      active: c.active,
    }));

  const save = useMutation({
    mutationFn: () =>
      setKitComponents(
        kitId,
        // Project explicitly — the PUT's body zod is non-strict, so stray display
        // fields would be silently accepted and dropped rather than erroring.
        current
          .filter((r) => r.componentProductId)
          .map((r) => ({
            componentProductId: r.componentProductId,
            variantId: r.variantId,
            qtyPerKit: r.qtyPerKit,
          })),
      ),
    meta: { successMessage: 'Kit contents saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kit-components', kitId] }),
  });

  // Eligibility is now the server's job (`componentEligible` excludes kits and
  // bundles, `activeOnly` excludes archived) so the picker isn't bounded by one
  // pre-fetched catalog page. An already-saved archived component still labels
  // itself, from the `active` flag GET /components returns.
  // Variations for every distinct chosen component, fetched at SECTION level
  // rather than per row: `useQueries` is a single hook over a variable-length
  // array, so adding or removing a row can't shift the hook order, and two rows
  // naming the same product share one request.
  const compIds = [...new Set(current.map((r) => r.componentProductId).filter(Boolean))];
  const variantQueries = useQueries({
    queries: compIds.map((id) => ({
      queryKey: ['house-variants', id],
      queryFn: () => listHouseVariants(id),
    })),
  });
  const variantsByProduct = new Map(
    compIds.map((id, i) => [id, (variantQueries[i]?.data ?? []).filter((v) => v.active)]),
  );

  // A product WITH variations has no stock of its own, so an inclusion must name
  // one — the same rule the server enforces as `component_variant_required`.
  // Blocking Save here means the two can't disagree. Only judge a row once its
  // variation list has actually loaded, or Save flickers disabled on open.
  const missingVariant = current.some(
    (r) =>
      r.componentProductId &&
      (variantsByProduct.get(r.componentProductId)?.length ?? 0) > 0 &&
      !r.variantId,
  );

  const patch = (i: number, next: Partial<ComponentDraft>) =>
    setRows(current.map((x, j) => (j === i ? { ...x, ...next } : x)));

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        If this kit runs out of its own stock at a branch, selling it deducts these items instead.
        An item that has variations must name which one.
      </p>
      {componentsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-1">Item</th>
              <th className="py-1 w-56">Variation</th>
              <th className="py-1 w-28">Qty per kit</th>
              <th className="py-1 w-16" />
            </tr>
          </thead>
          <tbody>
            {current.map((r, i) => (
              <ComponentRow
                key={i}
                row={r}
                kitId={kitId}
                isBundle={isBundle}
                chosenIds={compIds}
                variants={variantsByProduct.get(r.componentProductId) ?? []}
                onChange={(next) => patch(i, next)}
                onRemove={() => setRows(current.filter((_, j) => j !== i))}
              />
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setRows([
              ...current,
              {
                componentProductId: '',
                variantId: null,
                qtyPerKit: 1,
                sku: '',
                name: '',
                active: true,
              },
            ])
          }
        >
          + Add item
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={save.isPending || missingVariant}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save contents'}
        </Button>
        {missingVariant && (
          <span className="text-xs text-destructive">
            Pick a variation for every item that has them.
          </span>
        )}
        {save.isSuccess && !missingVariant && <span className="text-xs text-success">Saved</span>}
      </div>
    </div>
  );
}

/** One inclusion row. Purely presentational — the parent owns all the data. */
function ComponentRow({
  row,
  kitId,
  isBundle,
  chosenIds,
  variants,
  onChange,
  onRemove,
}: {
  row: ComponentDraft;
  /** Never selectable: the server rejects it as `component_cannot_be_self`. */
  kitId: string;
  /** A bundle may include other bundles (a box of packs); a kit may not. */
  isBundle: boolean;
  /** Already-used product ids — annotated, NOT excluded (see below). */
  chosenIds: string[];
  /** Active variations on the chosen product; empty = plain product, no picker. */
  variants: HouseVariant[];
  onChange: (next: Partial<ComponentDraft>) => void;
  onRemove: () => void;
}) {
  const needsVariant = variants.length > 0 && !row.variantId;

  return (
    <tr className="border-b last:border-0">
      <td className="py-1 pr-2">
        <ProductPicker
          selected={
            row.componentProductId
              ? {
                  id: row.componentProductId,
                  sku: row.sku,
                  name: row.name,
                  active: row.active,
                }
              : null
          }
          onChange={(p) =>
            // Changing the product invalidates any variation picked under the
            // old one.
            onChange({
              componentProductId: p?.id ?? '',
              sku: p?.sku ?? '',
              name: p?.name ?? '',
              active: p?.active ?? true,
              variantId: null,
            })
          }
          excludeIds={[kitId]}
          filter={{ activeOnly: true, componentEligible: true, componentAllowBundles: isBundle }}
          // Annotate rather than exclude: the server dedupes on
          // `productId:variantId`, so two VARIATIONS of one parent are two
          // legitimate inclusions — hiding a chosen product would make such a
          // kit unbuildable.
          annotate={(p) => (chosenIds.includes(p.id) ? 'already in this kit' : null)}
          placeholder="Search item by name or SKU…"
        />
      </td>
      <td className="py-1 pr-2">
        {variants.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <Select
            value={row.variantId ?? '__none__'}
            onValueChange={(v) => onChange({ variantId: v === '__none__' ? null : v })}
          >
            <SelectTrigger className={cn('h-9', needsVariant && 'border-destructive')}>
              <SelectValue placeholder="Select variation…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select variation…</SelectItem>
              {variants.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name} ({v.sku})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="py-1 pr-2">
        <Input
          type="number"
          min="1"
          value={row.qtyPerKit}
          onChange={(e) => onChange({ qtyPerKit: Math.max(1, Number(e.target.value) || 1) })}
        />
      </td>
      <td className="py-1">
        <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

/**
 * House product variations editor. Add variants (name/price/pv/srp), edit
 * price/pv/active inline, and set per-branch stock. PV is entered directly
 * (house goods have no commission to derive it from).
 */
type RowDraft = {
  sku: string;
  price: string;
  pv: string;
  srp: string;
  priceGc: string;
  priceCoin: string;
  /** Accounting Phase 1: unit-cost override (blank = inherit product). */
  cost: string;
  coinGrant: string;
  binaryPoints: string;
  vortexToken: string;
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  branchId: string;
  stockQty: string;
};

/** Row fields persisted by the PATCH half of "Save changes" (stock is separate). */
const ROW_PATCH_KEYS: (keyof RowDraft)[] = [
  'sku',
  'price',
  'pv',
  'srp',
  'priceGc',
  'priceCoin',
  'cost',
  'coinGrant',
  'binaryPoints',
  'vortexToken',
  'weightKg',
  'lengthCm',
  'widthCm',
  'heightCm',
];

const rowOvStr = (v: string | null) => (v == null ? '' : String(Number(v)));
const rowOrNull = (s: string) => (s.trim() === '' ? null : s.trim());

/** Seed a row draft from the server variant (stock starts blank per-branch). */
function seedRowDraft(v: import('../lib/api').HouseVariant, defaultBranch: string): RowDraft {
  return {
    sku: v.sku,
    price: String(Number(v.price)),
    pv: String(Number(v.pv)),
    srp: v.srp ? String(Number(v.srp)) : '',
    priceGc: rowOvStr(v.priceGc),
    priceCoin: rowOvStr(v.priceCoin),
    cost: rowOvStr(v.cost),
    coinGrant: rowOvStr(v.coinGrant),
    binaryPoints: rowOvStr(v.binaryPoints),
    vortexToken: rowOvStr(v.vortexToken),
    weightKg: rowOvStr(v.weightKg),
    lengthCm: rowOvStr(v.lengthCm),
    widthCm: rowOvStr(v.widthCm),
    heightCm: rowOvStr(v.heightCm),
    branchId: defaultBranch,
    stockQty: '',
  };
}

function VariantsSection({ productId, product }: { productId: string; product: Product }) {
  const qc = useQueryClient();
  // Variants have no purchasable columns of their own — they inherit the
  // product's sale intent, so a variation can't be priced in a currency the
  // product isn't sold in. (`redeemOnly` is always GC; mirrors the info form.)
  const gcSold = product.gcPurchasable || product.redeemOnly;
  const coinSold = product.coinPurchasable;
  // Same rule as the info form: a virtual product/service ships nothing, so no
  // variation of it has dimensions either. Presentation only — variants have no
  // is_virtual column (by design) and `computeParcel` already skips the whole
  // product, so any previously stored variant dimensions were already inert.
  const isVirtual = product.isVirtual === true;
  const variantsQ = useQuery({
    queryKey: ['house-variants', productId],
    queryFn: () => listHouseVariants(productId),
  });
  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: listBranches });
  // Accounting Phase 1: per-variant unit-cost override, shown only for
  // accounting tenants (same gate as the product-level cost field).
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const showCost = featuresQ.data?.accounting_enabled === true;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['house-variants', productId] });
  const blankDraft = {
    name: '',
    sku: '',
    price: '',
    pv: '',
    srp: '',
    priceGc: '',
    priceCoin: '',
    cost: '',
    coinGrant: '',
    binaryPoints: '',
    vortexToken: '',
    weightKg: '',
    lengthCm: '',
    widthCm: '',
    heightCm: '',
  };
  const [draft, setDraft] = useState(blankDraft);
  const [advOpen, setAdvOpen] = useState(false);
  const orNull = (s: string) => (s.trim() === '' ? null : s.trim());
  const setD = (k: keyof typeof blankDraft, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const add = useMutation({
    mutationFn: () =>
      addHouseVariant(productId, {
        name: draft.name,
        // Blank → the server derives `<PARENT>-<OPTION>` from the parent sku.
        sku: draft.sku.trim() ? draft.sku.trim().toUpperCase() : undefined,
        price: draft.price,
        pv: draft.pv || undefined,
        srp: draft.srp || undefined,
        priceGc: orNull(draft.priceGc),
        priceCoin: orNull(draft.priceCoin),
        cost: orNull(draft.cost),
        coinGrant: orNull(draft.coinGrant),
        binaryPoints: orNull(draft.binaryPoints),
        vortexToken: orNull(draft.vortexToken),
        weightKg: orNull(draft.weightKg),
        lengthCm: orNull(draft.lengthCm),
        widthCm: orNull(draft.widthCm),
        heightCm: orNull(draft.heightCm),
      }),
    meta: { successMessage: 'Variation added.' },
    onSuccess: () => {
      setDraft(blankDraft);
      setAdvOpen(false);
      invalidate();
    },
  });

  // `?? []` mints a new array every render, which would re-run the seeding
  // effect below on each one.
  const variants = useMemo(() => variantsQ.data ?? [], [variantsQ.data]);
  const branches = branchesQ.data ?? [];
  const defaultBranch = branches[0]?.id ?? '';

  // One draft per variant row; a single "Save changes" persists every dirty row.
  const [rowDrafts, setRowDrafts] = useState<Record<string, RowDraft>>({});
  useEffect(() => {
    setRowDrafts((prev) => {
      const next: Record<string, RowDraft> = {};
      for (const v of variants) next[v.id] = prev[v.id] ?? seedRowDraft(v, defaultBranch);
      return next;
    });
  }, [variants, defaultBranch]);
  const setRowField = (id: string, k: keyof RowDraft, val: string) =>
    setRowDrafts((m) => {
      const cur = m[id];
      if (!cur) return m;
      return { ...m, [id]: { ...cur, [k]: val } };
    });

  const rowIsDirty = (v: import('../lib/api').HouseVariant): boolean => {
    const d = rowDrafts[v.id];
    if (!d) return false;
    const base = seedRowDraft(v, d.branchId);
    const pricingDirty = ROW_PATCH_KEYS.some((k) => d[k] !== base[k]);
    const stockDirty = !!d.branchId && d.stockQty.trim() !== '';
    return pricingDirty || stockDirty;
  };
  const dirtyCount = variants.filter(rowIsDirty).length;

  const saveAll = useMutation({
    mutationFn: async () => {
      const failed: string[] = [];
      let saved = 0;
      for (const v of variants) {
        const d = rowDrafts[v.id];
        if (!d) continue;
        const base = seedRowDraft(v, d.branchId);
        const pricingDirty = ROW_PATCH_KEYS.some((k) => d[k] !== base[k]);
        const stockDirty = !!d.branchId && d.stockQty.trim() !== '';
        if (!pricingDirty && !stockDirty) continue;
        try {
          if (pricingDirty) {
            await updateHouseVariant(productId, v.id, {
              sku: d.sku.trim().toUpperCase(),
              price: d.price,
              pv: d.pv,
              srp: d.srp || null,
              priceGc: rowOrNull(d.priceGc),
              priceCoin: rowOrNull(d.priceCoin),
              cost: rowOrNull(d.cost),
              coinGrant: rowOrNull(d.coinGrant),
              binaryPoints: rowOrNull(d.binaryPoints),
              vortexToken: rowOrNull(d.vortexToken),
              weightKg: rowOrNull(d.weightKg),
              lengthCm: rowOrNull(d.lengthCm),
              widthCm: rowOrNull(d.widthCm),
              heightCm: rowOrNull(d.heightCm),
            });
          }
          if (stockDirty) {
            await setHouseVariantStock(productId, v.id, d.branchId, Number(d.stockQty));
          }
          saved++;
        } catch {
          failed.push(v.name);
        }
      }
      return { saved, failed };
    },
    meta: { successMessage: 'Variations saved.' },
    onSuccess: () => {
      setRowDrafts({});
      invalidate();
    },
  });

  return (
    <div className="space-y-4">
      <VariationAxesEditor productId={productId} onApplied={invalidate} />
      {variants.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Combination matrix — set a price per variation
            <span className="ml-1 normal-case font-normal lowercase">
              (Coin ₵ = coin price, Grant = coins awarded on purchase; use{' '}
              <span className="font-mono">⚙ More</span> for GC price, vortex, binary points
              {isVirtual ? '' : ', weight & dimensions'};{' '}
              <span className="font-mono">% Discounts</span> for per-variation
              membership/rank/stockist prices; stock saves to the selected branch)
            </span>
          </p>
          <CurrencyGateNote gcSold={gcSold} coinSold={coinSold} />
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-2">Variation</th>
                <th className="py-1 pr-2">Price</th>
                <th className="py-1 pr-2">SRP</th>
                <th className="py-1 pr-2">PV</th>
                <th className="py-1 pr-2">Coin ₵</th>
                <th className="py-1 pr-2">Grant</th>
                <th className="py-1 pr-2">Active</th>
                <th className="py-1 pr-2">Stock</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => {
                const d = rowDrafts[v.id];
                return d ? (
                  <VariantRow
                    key={v.id}
                    productId={productId}
                    variant={v}
                    branches={branches}
                    draft={d}
                    gcSold={gcSold}
                    coinSold={coinSold}
                    isVirtual={isVirtual}
                    showCost={showCost}
                    onField={(k, val) => setRowField(v.id, k, val)}
                    onChange={invalidate}
                  />
                ) : null;
              })}
            </tbody>
          </table>
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              disabled={saveAll.isPending || dirtyCount === 0}
              onClick={() => saveAll.mutate()}
            >
              {saveAll.isPending
                ? 'Saving…'
                : dirtyCount > 0
                  ? `Save changes (${dirtyCount})`
                  : 'Save changes'}
            </Button>
            {saveAll.isSuccess && saveAll.data.failed.length === 0 && (
              <span className="text-xs text-success">Saved {saveAll.data.saved}</span>
            )}
            {saveAll.data && saveAll.data.failed.length > 0 && (
              <span className="text-xs text-destructive">
                Saved {saveAll.data.saved}; failed: {saveAll.data.failed.join(', ')}
              </span>
            )}
          </div>
        </div>
      )}
      <div className="space-y-1 border-t pt-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Or add a single variation manually (for one-off SKUs without axes):
          </p>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setAdvOpen((v) => !v)}
          >
            {advOpen ? '▾ Fewer fields' : '⚙ More fields'}
          </button>
        </div>
        <div className="grid grid-cols-[1fr_130px_90px_90px_70px_90px_90px_auto] items-end gap-2">
          <LabeledInput label="Name" value={draft.name} onChange={(v) => setD('name', v)} />
          {/* Blank is fine — the server derives `<PARENT>-<NAME>` from the product's sku. */}
          <LabeledInput
            label="SKU (optional)"
            value={draft.sku}
            onChange={(v) => setD('sku', v.toUpperCase())}
          />
          <LabeledInput
            label="Price"
            money
            value={draft.price}
            onChange={(v) => setD('price', v)}
          />
          <LabeledInput label="SRP" money value={draft.srp} onChange={(v) => setD('srp', v)} />
          <LabeledInput label="PV" money value={draft.pv} onChange={(v) => setD('pv', v)} />
          <LabeledInput
            label="Coin ₵"
            money
            value={draft.priceCoin}
            onChange={(v) => setD('priceCoin', v)}
            disabled={!coinSold}
          />
          <LabeledInput
            label="Grant"
            money
            value={draft.coinGrant}
            onChange={(v) => setD('coinGrant', v)}
          />
          <Button
            type="button"
            size="sm"
            disabled={add.isPending || !draft.name || !draft.price}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Adding…' : '+ Add variation'}
          </Button>
        </div>
        <CurrencyGateNote gcSold={gcSold} coinSold={coinSold} />
        {advOpen && (
          <div className="flex flex-wrap gap-3 rounded-md bg-muted/20 p-2">
            <AdvField
              label="GC price"
              value={draft.priceGc}
              onChange={(v) => setD('priceGc', v)}
              disabled={!gcSold}
            />
            <AdvField
              label="Binary pts"
              value={draft.binaryPoints}
              onChange={(v) => setD('binaryPoints', v)}
            />
            <AdvField
              label="Vortex"
              value={draft.vortexToken}
              onChange={(v) => setD('vortexToken', v)}
            />
            {showCost && (
              <AdvField label="Unit cost" value={draft.cost} onChange={(v) => setD('cost', v)} />
            )}
            {!isVirtual && (
              <>
                <AdvField
                  label="Weight (g)"
                  value={draft.weightKg}
                  onChange={(v) => setD('weightKg', v)}
                />
                <AdvField
                  label="Length (cm)"
                  value={draft.lengthCm}
                  onChange={(v) => setD('lengthCm', v)}
                />
                <AdvField
                  label="Width (cm)"
                  value={draft.widthCm}
                  onChange={(v) => setD('widthCm', v)}
                />
                <AdvField
                  label="Height (cm)"
                  value={draft.heightCm}
                  onChange={(v) => setD('heightCm', v)}
                />
              </>
            )}
            <span className="self-end text-[11px] text-muted-foreground">
              blank = inherit product (GC: not sold)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pick which Variation Types (axes) this product varies by + which option values
 * it offers, then regenerate the combination grid. Saving creates a variant row
 * per combination and deactivates combinations no longer offered.
 */
function VariationAxesEditor({
  productId,
  onApplied,
}: {
  productId: string;
  onApplied: () => void;
}) {
  const typesQ = useQuery({ queryKey: ['variation-types'], queryFn: () => listVariationTypes() });
  const configQ = useQuery({
    queryKey: ['product-variations', productId],
    queryFn: () => getProductVariations(productId),
  });
  const qc = useQueryClient();
  // typeId -> Set of offered optionIds. null entry means the axis isn't used.
  const [sel, setSel] = useState<Record<string, string[]> | null>(null);

  const types = typesQ.data ?? [];
  // Seed local state from server config once both queries resolve.
  const serverSel = configQ.data?.selections;
  if (sel === null && serverSel) {
    const seed: Record<string, string[]> = {};
    for (const s of serverSel) seed[s.typeId] = s.optionIds;
    setSel(seed);
  }
  const current = sel ?? {};

  const save = useMutation({
    mutationFn: () => {
      const selections: ProductVariationSelection[] = Object.entries(current)
        .filter(([, optionIds]) => optionIds.length > 0)
        .map(([typeId, optionIds]) => ({ typeId, optionIds }));
      return setProductVariations(productId, selections);
    },
    meta: { successMessage: 'Variation matrix generated.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-variations', productId] });
      onApplied();
    },
  });

  function toggleType(typeId: string, on: boolean) {
    setSel((prev) => {
      const next = { ...(prev ?? {}) };
      if (on) next[typeId] = next[typeId] ?? [];
      else delete next[typeId];
      return next;
    });
  }
  function toggleOption(typeId: string, optionId: string, on: boolean) {
    setSel((prev) => {
      const next = { ...(prev ?? {}) };
      const set = new Set(next[typeId] ?? []);
      if (on) set.add(optionId);
      else set.delete(optionId);
      next[typeId] = [...set];
      return next;
    });
  }

  if (typesQ.isLoading || configQ.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading variation types…</p>;
  }
  if (types.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No variation types defined yet. Create them under Commerce → Variation Types to build a
        size/color matrix.
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Variation axes
      </p>
      {types.map((t) => {
        const used = t.id in current;
        return (
          <div key={t.id} className="space-y-1">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={used} onCheckedChange={(v) => toggleType(t.id, v === true)} />
              <span className="font-medium">{t.name}</span>
            </label>
            {used && (
              <div className="ml-6 flex flex-wrap gap-1.5">
                {t.options.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    No values for this type yet.
                  </span>
                )}
                {t.options.map((o) => {
                  const on = (current[t.id] ?? []).includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggleOption(t.id, o.id, !on)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs ${
                        on ? 'border-primary bg-primary/10 font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-2 pt-1">
        <Button type="button" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Generating…' : 'Generate matrix'}
        </Button>
        <span className="text-[11px] text-muted-foreground">
          Creates a row per combination; removed combinations are hidden, not deleted.
        </span>
      </div>
      {save.error != null && (
        <p className="text-xs text-destructive">
          {apiErrorMessage(save.error, 'Failed to generate')}
        </p>
      )}
    </div>
  );
}

type SourcingDraft = {
  supplierId: string;
  variantId: string | null;
  unitCost: string;
  isPreferred: boolean;
};

/**
 * Sourcing list for a product: which suppliers carry it and at what buy cost.
 * `variantId === null` = applies to all variations; a row with a variant is a
 * per-variation override. Saving replaces the whole list (one PUT).
 */
function ProductSuppliersSection({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const sourcingQ = useQuery({
    queryKey: ['product-suppliers', productId],
    queryFn: () => listProductSuppliers(productId),
  });
  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => listSuppliers(true),
  });
  const variantsQ = useQuery({
    queryKey: ['house-variants', productId],
    queryFn: () => listHouseVariants(productId),
  });
  const [rows, setRows] = useState<SourcingDraft[] | null>(null);

  if (rows === null && sourcingQ.data) {
    setRows(
      sourcingQ.data.map((r) => ({
        supplierId: r.supplierId,
        variantId: r.variantId,
        unitCost: String(Number(r.unitCost)),
        isPreferred: r.isPreferred,
      })),
    );
  }
  const draft = rows ?? [];
  const suppliers = suppliersQ.data ?? [];
  const variants = variantsQ.data ?? [];

  const save = useMutation({
    mutationFn: () =>
      setProductSuppliers(
        productId,
        draft
          .filter((r) => r.supplierId)
          .map((r) => ({
            supplierId: r.supplierId,
            variantId: r.variantId,
            unitCost: r.unitCost || '0',
            isPreferred: r.isPreferred,
          })),
      ),
    meta: { successMessage: 'Sourcing saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-suppliers', productId] }),
  });

  function patch(i: number, p: Partial<SourcingDraft>) {
    setRows((prev) => (prev ?? []).map((r, j) => (j === i ? { ...r, ...p } : r)));
  }

  if (suppliers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No suppliers yet. Create them under Commerce → Suppliers, then link them here.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        A product can be sourced from several suppliers, each with its own buy cost. Leave the scope
        as “All variations”, or override a specific variation.
      </p>
      {draft.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1 pr-2">Supplier</th>
              <th className="py-1 pr-2">Scope</th>
              <th className="py-1 pr-2">Buy cost</th>
              <th className="py-1 pr-2">Preferred</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {draft.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="py-1 pr-2">
                  <select
                    value={r.supplierId}
                    onChange={(e) => patch(i, { supplierId: e.target.value })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="">— Select —</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  <select
                    value={r.variantId ?? ''}
                    onChange={(e) => patch(i, { variantId: e.target.value || null })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="">All variations</option>
                    {variants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  <MoneyInput
                    className="h-8 w-24"
                    value={r.unitCost}
                    onChange={(e) => patch(i, { unitCost: e.target.value })}
                  />
                </td>
                <td className="py-1 pr-2 text-center">
                  <input
                    type="checkbox"
                    checked={r.isPreferred}
                    onChange={(e) => patch(i, { isPreferred: e.target.checked })}
                  />
                </td>
                <td className="py-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setRows((prev) => (prev ?? []).filter((_, j) => j !== i))}
                  >
                    ✕
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setRows((prev) => [
              ...(prev ?? []),
              { supplierId: '', variantId: null, unitCost: '0', isPreferred: false },
            ])
          }
        >
          + Add supplier
        </Button>
        <Button type="button" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save sourcing'}
        </Button>
        {save.isSuccess && <span className="text-xs text-green-600">Saved</span>}
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  money,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  money?: boolean;
  disabled?: boolean;
}) {
  const Comp = money ? MoneyInput : Input;
  return (
    <div className={`space-y-1 ${disabled ? 'opacity-50' : ''}`}>
      <label className="text-xs text-muted-foreground">{label}</label>
      <Comp
        className="h-8"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Why a variation's GC / Coin price field is greyed out: sale intent lives on
 * the PRODUCT (`gcPurchasable` / `coinPurchasable`) and variants inherit it.
 */
function CurrencyGateNote({ gcSold, coinSold }: { gcSold: boolean; coinSold: boolean }) {
  if (gcSold && coinSold) return null;
  return (
    <p className="text-[11px] text-muted-foreground">
      {!gcSold && 'Enable “Sell in GC” on the product to price variations in GC. '}
      {!coinSold && 'Enable “Sell in Coin” on the product to price variations in Coin.'}
    </p>
  );
}

function VariantRow({
  productId,
  variant,
  branches,
  draft,
  gcSold,
  coinSold,
  isVirtual,
  showCost,
  onField,
  onChange,
}: {
  productId: string;
  variant: import('../lib/api').HouseVariant;
  branches: import('../lib/api').Branch[];
  draft: RowDraft;
  /** Inherited from the parent product — variants carry no purchasable flags. */
  gcSold: boolean;
  coinSold: boolean;
  /** Inherited too: a virtual product's variations have no dimensions. */
  isVirtual: boolean;
  /** Accounting Phase 1: whether the per-variant unit-cost override is shown. */
  showCost: boolean;
  onField: (k: keyof RowDraft, value: string) => void;
  onChange: () => void;
}) {
  const [adv, setAdv] = useState(false);
  const [discountsOpen, setDiscountsOpen] = useState(false);
  // Active/Hidden is a distinct instant toggle, not part of the batch Save.
  const toggle = useMutation({
    mutationFn: () => updateHouseVariant(productId, variant.id, { active: !variant.active }),
    meta: { successMessage: 'Variation updated.' },
    onSuccess: onChange,
  });

  return (
    <>
      <tr className="border-t align-top">
        <td className="py-1 pr-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              className={`whitespace-nowrap rounded border px-1 text-[10px] ${adv ? 'bg-muted text-foreground' : 'text-muted-foreground'} hover:text-foreground`}
              title={`GC price, points, vortex${isVirtual ? '' : ', weight & dimensions'} for this variation`}
              onClick={() => setAdv((v) => !v)}
            >
              {adv ? '▾ Less' : '⚙ More'}
            </button>
            <button
              type="button"
              className="whitespace-nowrap rounded border px-1 text-[10px] text-muted-foreground hover:text-foreground"
              title="Per-variation discount prices (membership / rank / stockist) for this variation"
              onClick={() => setDiscountsOpen(true)}
            >
              % Discounts
            </button>
            <span>
              {variant.name}
              {/* The variation's own scannable code. The POS matches a barcode by
                  full-string SKU compare, so this has to be settable — auto-
                  generated variants used to carry an unprintable `V-<uuid>`. */}
              <Input
                aria-label={`SKU for ${variant.name}`}
                className="mt-0.5 h-6 w-40 font-mono text-xs"
                value={draft.sku}
                onChange={(e) => onField('sku', e.target.value.toUpperCase())}
              />
            </span>
          </div>
          <Dialog open={discountsOpen} onOpenChange={setDiscountsOpen}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Discounts — {variant.name}</DialogTitle>
                <DialogDescription>
                  Absolute prices per membership tier, rank, and stockist for this variation. Lowest
                  applicable price wins; blank = the variation&apos;s own price.
                </DialogDescription>
              </DialogHeader>
              <VariantDiscountEditor
                productId={productId}
                variantId={variant.id}
                basePrice={variant.price}
              />
            </DialogContent>
          </Dialog>
        </td>
        <td className="py-1 pr-2">
          <MoneyInput
            className="w-20 h-8"
            value={draft.price}
            onChange={(e) => onField('price', e.target.value)}
          />
        </td>
        <td className="py-1 pr-2">
          <MoneyInput
            className="w-20 h-8"
            value={draft.srp}
            onChange={(e) => onField('srp', e.target.value)}
          />
        </td>
        <td className="py-1 pr-2">
          <MoneyInput
            className="w-16 h-8"
            value={draft.pv}
            onChange={(e) => onField('pv', e.target.value)}
          />
        </td>
        <td className="py-1 pr-2">
          <MoneyInput
            className={`w-20 h-8 ${coinSold ? '' : 'opacity-50'}`}
            placeholder={coinSold ? 'not sold' : '—'}
            value={draft.priceCoin}
            onChange={(e) => onField('priceCoin', e.target.value)}
            disabled={!coinSold}
          />
        </td>
        <td className="py-1 pr-2">
          <MoneyInput
            className="w-20 h-8"
            placeholder="inherit"
            value={draft.coinGrant}
            onChange={(e) => onField('coinGrant', e.target.value)}
          />
        </td>
        <td className="py-1 pr-2">
          <Button
            type="button"
            size="sm"
            variant={variant.active ? 'outline' : 'ghost'}
            onClick={() => toggle.mutate()}
          >
            {variant.active ? 'Active' : 'Hidden'}
          </Button>
        </td>
        <td className="py-1 pr-2">
          <div className="flex items-center gap-1">
            <Select value={draft.branchId} onValueChange={(v) => onField('branchId', v)}>
              <SelectTrigger className="h-8 w-28">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <MoneyInput
              integer
              className="w-16 h-8"
              placeholder="qty"
              value={draft.stockQty}
              onChange={(e) => onField('stockQty', e.target.value)}
            />
          </div>
        </td>
      </tr>
      {adv && (
        <tr className="bg-muted/20">
          <td colSpan={8} className="px-3 py-2">
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Per-variation overrides — blank = inherit product (GC price: blank = not sold in that
              currency)
            </p>
            <CurrencyGateNote gcSold={gcSold} coinSold={coinSold} />
            <div className="flex flex-wrap gap-3">
              <AdvField
                label="GC price"
                value={draft.priceGc}
                onChange={(v) => onField('priceGc', v)}
                disabled={!gcSold}
              />
              <AdvField
                label="Binary pts"
                value={draft.binaryPoints}
                onChange={(v) => onField('binaryPoints', v)}
              />
              <AdvField
                label="Vortex"
                value={draft.vortexToken}
                onChange={(v) => onField('vortexToken', v)}
              />
              {showCost && (
                <AdvField
                  label="Unit cost"
                  value={draft.cost}
                  onChange={(v) => onField('cost', v)}
                />
              )}
              {!isVirtual && (
                <>
                  <AdvField
                    label="Weight (g)"
                    value={draft.weightKg}
                    onChange={(v) => onField('weightKg', v)}
                  />
                  <AdvField
                    label="Length (cm)"
                    value={draft.lengthCm}
                    onChange={(v) => onField('lengthCm', v)}
                  />
                  <AdvField
                    label="Width (cm)"
                    value={draft.widthCm}
                    onChange={(v) => onField('widthCm', v)}
                  />
                  <AdvField
                    label="Height (cm)"
                    value={draft.heightCm}
                    onChange={(v) => onField('heightCm', v)}
                  />
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AdvField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`space-y-1 ${disabled ? 'opacity-50' : ''}`}>
      <label className="text-[10px] text-muted-foreground">{label}</label>
      <MoneyInput
        className="h-8 w-24"
        placeholder="inherit"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}
